import { formatHrBookingRef } from "../bookingRefDisplay";
import { normalizeSearchToken } from "../../features/operationRequest/operationRequestSearchMatch";
import { resolveLdsOrderDisplayRef } from "../resolveLdsOrderDisplayRef";
import { formatMergedOrderHrLabel, resolveMergedOrderUx } from "./mergedLdsOrderUx";

export const ORDER_SEARCH_CATEGORY_TRACE_TAG = "[ORDER_SEARCH_CATEGORY_TRACE]";

/**
 * @param {unknown} value
 * @returns {{
 *   input: string,
 *   rawNumber: string,
 *   hrNumber: string,
 *   searchToken: string,
 *   searchDigits: string,
 * }}
 */
export function normalizeOrderSearchTerm(value) {
  let input = String(value ?? "").trim();
  if (!input) {
    return {
      input: "",
      rawNumber: "",
      hrNumber: "",
      searchToken: "",
      searchDigits: "",
    };
  }

  const compact = input.toUpperCase().replace(/\s+/g, "").replace(/-/g, "");
  let body = compact;
  while (body.startsWith("HR")) {
    body = body.slice(2);
  }

  const searchDigits = body.replace(/\D/g, "");
  const rawNumber = searchDigits || body;
  const hrNumber = searchDigits ? `HR-${searchDigits}` : "";
  const searchToken = normalizeSearchToken(value);

  return {
    input,
    rawNumber,
    hrNumber,
    searchToken,
    searchDigits,
  };
}

/** @param {Set<string>} out @param {...unknown} values */
function pushValues(out, ...values) {
  for (const v of values) {
    const s = v == null ? "" : String(v).trim();
    if (s) out.add(s);
  }
}

/**
 * @param {Record<string, unknown> | null | undefined} order
 * @returns {string[]}
 */
export function collectOrderRowSearchValues(order) {
  if (!order || typeof order !== "object") return [];
  const values = new Set();

  pushValues(
    values,
    order.id,
    order.orderid,
    order.order_id,
    order.orderId,
    order.dealid,
    order.deal_id,
    order.dealId,
    order.booking_ref,
    order.bookingRef,
    order.hr_order_number,
    order.hrOrderNumber,
    order.hotel_hr_order_number,
    order.display_order_number,
    order.displayOrderNumber,
    order.unifiedOrderNumber,
    order.unified_order_number,
    order.orderNumber,
    order.order_number
  );

  const bookingRaw = order.booking_ref ?? order.bookingRef;
  const hrFormatted = formatHrBookingRef(bookingRaw);
  if (hrFormatted) pushValues(values, hrFormatted);

  try {
    const ref = resolveLdsOrderDisplayRef(order);
    if (ref?.displayValue) pushValues(values, ref.displayValue);
  } catch {
    /* ignore */
  }

  const ux = resolveMergedOrderUx(order);
  pushValues(values, ux.mergedIntoOrderId, ux.mergeGroupId);
  if (ux.mergedIntoOrderId) {
    pushValues(values, formatMergedOrderHrLabel(ux.mergedIntoOrderId, order));
  }

  return [...values];
}

/**
 * @param {Record<string, unknown>} order
 * @param {ReturnType<typeof normalizeOrderSearchTerm>} normalized
 */
export function orderRowMatchesOrderSearchTerm(order, normalized) {
  if (!normalized?.searchToken && !normalized?.rawNumber) return true;

  const needleToken = normalized.searchToken;
  const needleDigits = normalized.searchDigits;
  const candidates = collectOrderRowSearchValues(order);

  for (const cand of candidates) {
    const tok = normalizeSearchToken(cand);
    if (!tok) continue;
    if (tok === needleToken) return true;
    if (needleToken.length >= 4 && (tok.includes(needleToken) || needleToken.includes(tok))) {
      return true;
    }
  }

  if (needleDigits && needleDigits.length >= 4) {
    for (const cand of candidates) {
      const digits = String(cand).replace(/\D/g, "");
      if (digits && digits.includes(needleDigits)) return true;
    }
    const hayDigits = candidates.map((c) => String(c).replace(/\D/g, "")).join("");
    if (hayDigits.includes(needleDigits)) return true;
  }

  return false;
}

/**
 * @param {Record<string, unknown>} payload
 */
export function emitOrderSearchCategoryTrace(payload) {
  if (!import.meta.env?.DEV || typeof console === "undefined" || !console.info) return;
  try {
    console.info(ORDER_SEARCH_CATEGORY_TRACE_TAG, payload);
  } catch {
    /* ignore */
  }
}

/**
 * Normalize order_id filter value before persisting to state / URL / API.
 * @param {unknown} value
 */
export function normalizeOrderIdFilterValue(value) {
  const norm = normalizeOrderSearchTerm(value);
  return norm.rawNumber || norm.input.replace(/\s+/g, "").replace(/^HR-?/i, "");
}
